import { Fragment } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import classNames from 'classnames';

interface Props {
  error?: string;
  id: string;
  label: string;
  options: string[];
  onChange: (selected: string) => void;
  selected: string;
}

export default function Select({
  error,
  id,
  label,
  options,
  onChange,
  selected,
}: Props) {
  return (
    <Listbox value={selected} onChange={onChange}>
      {({ open }) => (
        <div className="relative group">
          <Listbox.Label
            htmlFor={id}
            className={classNames(
              'absolute -top-2.5 left-6 h-4 inline-block bg-white px-1 text-11 leading-140 font-bold z-10 uppercase',
              {
                'text-blue-800': !error,
              },
              { 'text-error': error },
            )}>
            <div className="hidden group-focus-within:block absolute bottom-0 left-0 bg-iceblue-100 w-full h-1.5 z-0"></div>
            <span className="relative z-1">{label}</span>
          </Listbox.Label>
          <div className="relative">
            <Listbox.Button
              className={classNames(
                'relative w-full cursor-default text-15 leading-140 tracking-3 focus:ring-1 outline-none border rounded-xl md:rounded-3xl px-7 py-4',
                {
                  'border-blue-400 focus:bg-iceblue-100 focus:border-blue-400 focus:ring-blue-400 text-blue-800':
                    !error,
                },
                {
                  'border-error focus:border-error focus:ring-error text-error':
                    error,
                },
              )}>
              <span className="block text-left truncate">
                {selected || 'Please select an option'}
              </span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
                <ChevronUpDownIcon
                  className="h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
              </span>
            </Listbox.Button>

            <Transition
              show={open}
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0">
              <Listbox.Options className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-xl md:rounded-3xl bg-white py-1 text-base border border-blue-400 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                {options.map((option, index) => (
                  <Listbox.Option
                    key={index}
                    className={({ active }) =>
                      classNames(
                        { 'bg-blue-500 text-white': active },
                        { 'text-gray-900': !active },
                        'relative cursor-default select-none py-2 pl-3 pr-9',
                      )
                    }
                    value={option}>
                    {({ selected, active }) => (
                      <>
                        <span
                          className={classNames(
                            selected ? 'font-semibold' : 'font-normal',
                            'block truncate',
                          )}>
                          {option}
                        </span>

                        {selected ? (
                          <span
                            className={classNames(
                              active ? 'text-white' : 'text-blue-500',
                              'absolute inset-y-0 right-0 flex items-center pr-4',
                            )}>
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </div>
          {error && (
            <div className="text-error text-11 px-7 pt-1 font-medium">
              {error}
            </div>
          )}
        </div>
      )}
    </Listbox>
  );
}
